// src/features/system/devices/hooks/useDevicesList.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared hook for the device list query with resilient defaults:
//   • retry 2 with exponential back-off  → handles transient 5xx / 429
//   • staleTime 30 s                     → skip refetch on quick tab switches
//   • gcTime 5 min                       → survive unmount/remount cycles
//   • placeholderData from localStorage  → instant render on page load
//
// The localStorage cache breaks the data waterfall: the telemetry stream
// can fire immediately using cached device IDs rather than blocking until
// the /devices endpoint resolves (which depends on the orchestrator
// /scopes query — frequently 5-15s under burst load).
//
// All IoT panels that need `Device[]` should import this hook instead of
// calling `useQuery({ queryKey, queryFn })` inline.
// ────────────────────────────────────────────────────────────────────────────
import { useEffect } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { fetchDevicesApi, DEVICE_KEYS, type Device } from '../api/iotDevicesApi';

type Opts = Omit<UseQueryOptions<Device[], Error>, 'queryKey' | 'queryFn'>;

// ── localStorage persistence for instant page-load rendering ─────────────
const STORAGE_KEY = 'pac:devices:list';

function loadCachedDevices(): Device[] | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : undefined;
  } catch { return undefined; }
}

function persistDevices(devices: Device[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(devices)); }
  catch { /* quota exceeded or private mode — silent */ }
}

export function useDevicesList(opts?: Opts) {
  const result = useQuery<Device[], Error>({
    queryKey:        DEVICE_KEYS.list,
    queryFn:         fetchDevicesApi,
    staleTime:       30_000,
    gcTime:          5 * 60_000,
    retry:           2,
    retryDelay:      (attempt) => Math.min(1_000 * 2 ** attempt, 5_000),
    placeholderData: loadCachedDevices,
    // Auto-retry when the server returned an empty list — this covers the
    // cold-start scenario where the orchestrator is temporarily unavailable
    // and the BFF degrades both upstream calls to { items: [] }.
    // Once devices appear the interval stops (returns false).
    refetchInterval: (query) =>
      (query.state.data?.length === 0 && query.state.status !== 'pending') ? 10_000 : false,
    ...opts,
  });

  // Persist to localStorage on successful server fetch (not placeholder echo)
  useEffect(() => {
    if (result.data && !result.isPlaceholderData) {
      persistDevices(result.data);
    }
  }, [result.data, result.isPlaceholderData]);

  return result;
}
